import React from "react";
import { find, isEmpty, uniq, flatMap, filter } from "lodash";

import { useUser } from "../contexts/user";
import useCurriculumsQuery from "./useCurriculumsQuery";
import useUserAccessesQuery from "./useUserAccessesQuery";

function getAccessibleCurriculumsViaProfiles(user) {
  const selectedClassroomCurriculums = find(
    user.classrooms,
    (classroom) =>
      classroom.id === user.selectedClassroomId &&
      !isEmpty(classroom.curriculumAccesses) &&
      !isEmpty(classroom.curriculums),
  );

  if (!selectedClassroomCurriculums) {
    return [];
  }

  const accessibleCurriculumsIds = uniq(
    flatMap(selectedClassroomCurriculums.curriculumAccesses, "curriculums"),
  );

  return filter(
    selectedClassroomCurriculums.curriculums.allCurriculums,
    ({ id }) => accessibleCurriculumsIds.includes(id),
  );
}

function getAccessibleCurriculumsViaPiano(curriculums = [], userAccesses = []) {
  return curriculums
    .filter((curriculum) =>
      userAccesses.some(
        (access) => access.resource.rid === curriculum.pianoRid,
      ),
    )
    .map(({ id, name }) => ({ id, name }));
}

function getAccessibleCurriculums({ user, curriculums, userAccesses }) {
  if (!user?.isLoggedIn) {
    return [];
  }

  if (!user?.selectedClassroomId) {
    return [];
  }

  if (user?.features?.piano && user?.features?.pianoCurriculumAccess) {
    return getAccessibleCurriculumsViaPiano(curriculums, userAccesses);
  }

  return getAccessibleCurriculumsViaProfiles(user);
}

export default function useUserCurriculumsQuery(options = {}) {
  const { user } = useUser();
  const {
    curriculums,
    isCurriculumsLoading,
    curriculumsError,
  } = useCurriculumsQuery(options);
  const {
    userAccesses,
    isUserAccessesLoading,
    userAccessesError,
  } = useUserAccessesQuery({
    enabled: user?.features?.piano === true,
    ...options,
  });

  const userCurriculums = React.useMemo(
    () => getAccessibleCurriculums({ user, curriculums, userAccesses }),
    [curriculums, user, userAccesses],
  );

  return {
    userCurriculums,
    isUserCurriculumsLoading: isCurriculumsLoading || isUserAccessesLoading,
    userCurriculumsError: curriculumsError || userAccessesError,
  };
}
